import { useMemo, useState } from "react";
import "./App.css";
import services from "./services";

const money = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n || 0);

export default function App() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState("");
  const [jobAddress, setJobAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [discount25, setDiscount25] = useState(true);

  const categories = ["All", ...new Set(services.map((s) => s.category))];

  const filtered = useMemo(() => {
    return services.filter((s) => {
      const matchSearch = `${s.name} ${s.desc} ${s.category}`
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchCat = category === "All" || s.category === category;
      return matchSearch && matchCat;
    });
  }, [search, category]);

  const getUnitPrice = (price) => (discount25 ? price * 0.75 : price);

  const addItem = (item) => {
    setCart((prev) => {
      const exists = prev.find((x) => x.id === item.id);
      if (exists) {
        return prev.map((x) =>
          x.id === item.id ? { ...x, qty: x.qty + 1 } : x
        );
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const changeQty = (id, delta) => {
    setCart((prev) =>
      prev.map((x) =>
        x.id === id ? { ...x, qty: Math.max(1, x.qty + delta) } : x
      )
    );
  };

  const removeItem = (id) => {
    setCart((prev) => prev.filter((x) => x.id !== id));
  };

  const subtotal = cart.reduce(
    (sum, item) => sum + getUnitPrice(item.price) * item.qty,
    0
  );

  const quoteText = [
    "ONE VISION ELECTRIC",
    `Customer: ${customer || "-"}`,
    `Job Address: ${jobAddress || "-"}`,
    `Pricing Mode: ${discount25 ? "25% Discount Applied" : "Standard Pricing"}`,
    "",
    "LINE ITEMS:",
    ...cart.map(
      (item) =>
        `${item.qty} x ${item.name} @ ${money(getUnitPrice(item.price))} = ${money(
          getUnitPrice(item.price) * item.qty
        )}`
    ),
    "",
    `TOTAL: ${money(subtotal)}`,
    "",
    "RECOMMENDED OPTIONS:",
    "Good: Fix the issue",
    "Better: Upgrade for safety",
    "Best: Full system solution",
    "",
    `Notes: ${notes || "-"}`,
  ].join("\n");

  return (
    <div className="app">
      <div className="header">
        <h1 className="title">One Vision Electric ⚡</h1>
        <p className="subtitle">Visual Pricebook & Quote Builder</p>
      </div>

      <div className="panel">
        <input
          className="search"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="categories">
          {categories.map((cat) => (
            <button
              key={cat}
              className={category === cat ? "catBtn active" : "catBtn"}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <label className="toggleRow">
          <input
            type="checkbox"
            checked={discount25}
            onChange={(e) => setDiscount25(e.target.checked)}
          />
          <span>Apply 25% discount pricing</span>
        </label>
      </div>

      <div className="panel">
        <h2>Services</h2>
        <div className="cardGrid">
          {filtered.map((s) => (
            <div className="card" key={s.id}>
              {s.image && (
                <img src={s.image} alt={s.name} className="cardImg" />
              )}

              <div className="tag">{s.category}</div>
              <h3>{s.name}</h3>
              <p>{s.desc}</p>

              <div className="priceStack">
                {discount25 && <div className="oldPrice">{money(s.price)}</div>}
                <strong>{money(getUnitPrice(s.price))}</strong>
              </div>

              <div className="cardFooter">
                <button className="primaryBtn" onClick={() => addItem(s)}>
                  Add
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="panel">
        <h2>Quote / Proposal</h2>

        <input
          className="search"
          placeholder="Customer name"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
        />

        <input
          className="search secondInput"
          placeholder="Job address"
          value={jobAddress}
          onChange={(e) => setJobAddress(e.target.value)}
        />

        <textarea
          className="notes"
          placeholder="Job notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className="quoteBox">
          {cart.length === 0 && <p className="muted">No items added yet.</p>}

          {cart.map((item) => (
            <div key={item.id} className="quoteItem">
              <div className="quoteLeft">
                <div className="quoteName">{item.name}</div>
                <div className="muted">
                  {money(getUnitPrice(item.price))} each · Line total{" "}
                  {money(getUnitPrice(item.price) * item.qty)}
                </div>
              </div>

              <div className="quoteRight">
                <button className="smallBtn" onClick={() => changeQty(item.id, -1)}>
                  -
                </button>
                <span className="qty">{item.qty}</span>
                <button className="smallBtn" onClick={() => changeQty(item.id, 1)}>
                  +
                </button>
                <button className="removeBtn" onClick={() => removeItem(item.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="summaryBox">
            <div className="summaryRow">
              <span>Customer</span>
              <span>{customer || "-"}</span>
            </div>
            <div className="summaryRow">
              <span>Job Address</span>
              <span>{jobAddress || "-"}</span>
            </div>
            <div className="summaryRow">
              <span>Pricing Mode</span>
              <span>{discount25 ? "25% Off" : "Standard"}</span>
            </div>
            <div className="summaryRow totalRow">
              <span>Total</span>
              <span>{money(subtotal)}</span>
            </div>
          </div>

          <button
            className="copyBtn"
            onClick={() => navigator.clipboard.writeText(quoteText)}
          >
            Copy Quote
          </button>

          <div className="proposalNote">
            <h3>Proposal Notes</h3>
            <p>Pricing reflects the selected service scope and current pricing mode.</p>
            <p>Recommended upgrades can improve safety, reliability, and long-term value.</p>
          </div>
        </div>
      </div>

      <div className="panel">
        <h2>Recommended Upgrades</h2>
        <div className="upsellGrid">
          <div className="upsellCard">
            <h3>Good</h3>
            <p>Fix the issue</p>
          </div>
          <div className="upsellCard">
            <h3>Better</h3>
            <p>Upgrade for safety</p>
          </div>
          <div className="upsellCard">
            <h3>Best</h3>
            <p>Full system solution</p>
          </div>
        </div>
      </div>
    </div>
  );
}
